import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IResponse, ISystem, ISystemRole} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IDialogOptions} from "../../dialog/index";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {getSearchObjectParams} from "../../../util/request";
import {ObjectTypeId} from "../../../constants";
import {DELETING_MESSAGE, PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {ButtonManager} from "../../../util/ButtonManager";
import {createErrorMessage, isPartOf} from "../../../util/misc";
import {nsRoute} from "../../../util/route";
import {global} from "../../../appContext";
import {getCancelButton, getContinueButton} from "../../../constants/RouteDefinitions";
import ButtonOrder = nsRoute.ButtonOrder;
import purge_permissionHtml from './purge-permission.html?raw';

export const template = purge_permissionHtml;

interface IParams {
    dialog: IDialogOptions
    permission: ISystemRole
    system: ISystem
}

type TStep = 'init' | 'introduction' | 'process' | 'success' | 'error'

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    private readonly req_searchobject_A: PromiseRequest = new PromiseRequest('searchobject')
    private readonly req_searchobject_B: PromiseRequest = new PromiseRequest('searchobject')
    private readonly req_purgesystemrolepermissions: PromiseRequest = new PromiseRequest('purgesystemrolepermissions')
    private readonly req_deleterole: PromiseRequest = new PromiseRequest('deleterole')

    private numActiveUsers!: number;
    private numInactiveUsers!: number;
    private numTotalUsers!: number;

    private readonly pageButtons: ButtonManager = new ButtonManager()

    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null);
    private readonly toggleOverride: KnockoutObservable<boolean> = ko.observable(true);

    constructor(private params: IParams) {
        this.kosub.subscribe(this.step, this.onStep.bind(this))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => !this.isStep('process'),
            validator: () => !this.isStep('success', 'error'),
        }))

        this.pageButtons.add(getContinueButton({
            onClick: () => this.step('process'),
            cssClass: 'danger',
            enabled: () => this.isReady(),
            label: 'yes, continue',
            validator: () => this.isStep('introduction'),
        }))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => this.isReady(),
            icon: 'fa-times',
            label: 'close',
            order: ButtonOrder.CONTINUE,
            validator: () => this.isStep('success', 'error'),
        }))

        this.step('init')
    }

    private onStep() {
        switch (this.step()) {
            case "init": {
                this.isReady(false)
                this.messageData({message: PLEASE_WAIT_MESSAGE})

                const ruleset = nsRuleset.generateRuleset({
                    rules: [
                        nsRuleset.generateRule({
                            argument: '',
                            argumentoperator: 'Har verdi',
                            attribute: `[OBJECTPERMISSION.SYSTEMROLE;Name;Id;${this.params.permission.id}]`,
                        }),
                    ]
                })

                const searchObjectParams = getSearchObjectParams({
                    objecttypeid: ObjectTypeId.User,
                    jruleset: JSON.stringify(ruleset),
                });

                Promise.resolve()
                    .then(() => this.req_searchobject_A
                        .request(_.defaultsDeep({deleted: 1}, searchObjectParams), true)
                        .then((data: IResponse) => {
                            this.numActiveUsers = data.totalresults
                        })
                    )
                    .then(() => this.req_searchobject_B
                        .request(_.defaultsDeep({deleted: -1}, searchObjectParams), true)
                        .then((data: IResponse) => {
                            this.numInactiveUsers = data.totalresults
                        })
                    )
                    .then(() => {
                        this.numTotalUsers = this.numActiveUsers + this.numInactiveUsers
                        this.messageData(null)
                        this.isReady(true)
                        this.step("introduction")
                    })
                    .catch((reason: string) => {
                        this.step('error')
                        this.messageData(createErrorMessage(String(reason)))
                        this.params.dialog.onError?.()
                    })

                break
            }
            case 'process': {
                this.params.dialog.isClosable?.(false)
                this.isReady(false)
                this.messageData({message: DELETING_MESSAGE})

                Promise.resolve()
                    .then(() => this.req_purgesystemrolepermissions
                        .request({
                            systemroleid: this.params.permission.id,
                            includeoverrides: this.toggleOverride(),
                        })
                    )
                    .then(() => this.req_deleterole.request({
                        id: this.params.permission.id,
                    }))
                    .then(() => {
                        this.messageData(null)
                        this.isReady(true)
                        this.step("success")
                    })
                    .catch((reason: string) => {
                        this.step('error')
                        this.messageData(createErrorMessage(String(reason)))
                        this.params.dialog.onError?.()
                    })

                break
            }
            case 'success': {
                global.cacheManager.removeCache('searchsystem')
                global.cacheManager.removeCache('searchobject')
                global.cacheManager.removeCache('getobject')
                this.params.dialog.isClosable?.(true)
                this.messageData(null)
                this.isReady(true)

                this.params.dialog.onSuccess?.()

                break
            }
            case 'error': {
                this.params.dialog.isClosable?.(true)
                this.isReady(true)

                break
            }
        }
    }

    private isStep(...steps: TStep[]): boolean {
        const step = this.step();
        return step != null && isPartOf(step, ...steps)
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req_searchobject_A.abort()
        this.req_searchobject_B.abort()
        this.req_purgesystemrolepermissions.abort()
    }
}
